import { createFileRoute } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ManagedImage } from "@/components/site/ManagedImage";
import { PageHero } from "@/components/site/PageHero";
import { ButtonLink } from "@/components/site/Button";
import { canonical } from "@/lib/seo";
import { Phone, Check, Sun, Leaf, Building2, Users, Home, Briefcase, Wallet, Sparkles, Search, ShieldCheck, FileWarning, Minus, X } from "lucide-react";

const SLUG = "/roof-maintenance";
const TITLE = "Roof Maintenance Plans in Phoenix | Residential & Commercial | Durafoam Roofing";
const DESCRIPTION =
  "Durafoam Roofing offers roof maintenance plans for Phoenix homeowners, apartments, HOAs, and commercial properties. Two inspections per year, minor repairs included. Licensed ROC223900.";

const faqs: Array<{ q: string; a: string }> = [
  {
    q: "How much does the residential maintenance plan cost?",
    a: "Pricing depends on your roof size, type, and condition. We provide a written quote after your first free inspection. Contact us or schedule a free inspection and we will put together a plan that makes sense for your specific roof.",
  },
  {
    q: "What if you find something that needs a major repair during the maintenance visit?",
    a: "We will document it, photograph it, and walk you through exactly what we found. If it is a minor item covered in the plan we fix it on the spot. If it requires additional materials or significant labor we give you a written estimate before doing anything. You always approve before we proceed — no surprise charges.",
  },
  {
    q: "Do you maintain all roof types or just foam?",
    a: "We maintain foam roofs, tile roofs, shingle roofs, flat roofs, and rolled roofing systems. Our inspection checklist covers the specific items relevant to your roof type.",
  },
  {
    q: "How do I know when my foam roof needs recoating vs just maintenance?",
    a: "We assess foam coating condition at every maintenance visit and tell you honestly where you stand. If your coating is getting thin we will give you a timeline and a quote. Recoating is not part of the maintenance plan — it is a separate service — but knowing when it is coming lets you plan and budget for it rather than being surprised.",
  },
  {
    q: "Can I schedule maintenance for multiple properties?",
    a: "Yes. We work with investors, property managers, and HOAs managing multiple properties across the Phoenix Valley. Contact us to discuss a multi-property maintenance arrangement.",
  },
  {
    q: "What areas do you cover for maintenance plans?",
    a: "We offer maintenance plans throughout the Phoenix metro including Scottsdale, Mesa, Paradise Valley, Chandler, Tempe, Gilbert, Glendale, Peoria, Cave Creek, Carefree, Fountain Hills, Sun City, and all surrounding communities.",
  },
  {
    q: "What is not included in the maintenance plan?",
    a: "Major repairs, recoating, tile replacement (new tiles are an additional charge), significant flashing replacement, and anything requiring special-order materials are not included. We always tell you before doing any work that carries an additional cost.",
  },
];


const audiences = [
  { Icon: Home, title: "Apartment complexes", body: "Regular maintenance keeps small leaks from becoming tenant complaints and liability issues. We work around your residents." },
  { Icon: Users, title: "HOA communities", body: "We understand the documentation HOA boards need. Every visit includes a written report with photos suitable for board review." },
  { Icon: Building2, title: "Condo associations", body: "Common area roofing across multiple buildings. We coordinate visits to minimize disruption." },
  { Icon: Briefcase, title: "Commercial property owners and managers", body: "Roof failures cost far more in lost business and tenant relationships than maintenance ever does. We work with property managers on scheduling that minimizes disruption." },
  { Icon: Wallet, title: "Multi-property investors", body: "Consistent maintenance across your portfolio. One call, one relationship, consistent standards across all your properties." },
];

const prevention = [
  {
    small: "Minor bird hole near AC penetration — caught at maintenance visit, patched with roofing caulk. Included in plan.",
    big: "Water enters through hole during monsoon, saturates foam section, deck damage below. Foam replacement + interior repairs: $2,000–$6,000+.",
  },
  {
    small: "Failing sealant at scupper — re-sealed during maintenance visit. Included in plan.",
    big: "Scupper backs up during monsoon, water ponds against parapet, seeps into wall assembly. Structural repair required: $3,000–$8,000+.",
  },
  {
    small: "Cracked tile noted and documented, slipped tile reset — caught at maintenance visit.",
    big: "Exposed underlayment degrades from UV. Water infiltration at crack during monsoon. Underlayment replacement + interior drywall repair: $4,000–$12,000+.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Roof Maintenance Plan",
  provider: {
    "@type": "RoofingContractor",
    name: "Durafoam Roofing",
    url: "https://www.durafoaminc.com",
  },
  areaServed: "Phoenix Metropolitan Area, Arizona",
  description:
    "Residential and commercial roof maintenance plans including two inspections per year, minor repairs, drain and penetration checks, flashing inspection, and written condition reports.",
  serviceType: "Roof Maintenance",
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const Route = createFileRoute("/roof-maintenance")({
  head: () => {
    const url = canonical(SLUG);
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(serviceSchema) },
        { type: "application/ld+json", children: JSON.stringify(faqSchema) },
      ],
    };
  },
  component: Page,
});

function CheckItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2.5 text-[15px] leading-snug">
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
      <span className="text-foreground/85">{children}</span>
    </li>
  );
}

function Page() {
  return (
    <>
      <PageHero
        pageSlug="roof-maintenance"
        eyebrow="Roof Protection Plans"
        h1="Roof maintenance plans for Phoenix homes and properties"
        subhead="The smartest way to protect your roof investment — catch small problems before they become expensive ones."
        actions={
          <>
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Get a free inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" /> Call 480-941-5373
            </ButtonLink>
          </>
        }
      />


      {/* ROOF PROTECTION PLANS — PRICING */}
      <section className="relative overflow-hidden bg-[var(--surface)] py-16 lg:py-20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-[60%]"
          style={{
            background:
              "radial-gradient(ellipse at 50% 0%, rgba(27,43,107,0.08), transparent 70%)",
          }}
        />
        <Container className="relative">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Roof Protection Plans
            </span>
            <h2 className="mt-4 text-3xl font-bold text-primary sm:text-4xl">
              Protect Your Roof Before Problems Start
            </h2>
            <p className="mt-4 text-[17px] font-medium text-foreground/85">
              A small roof issue today can become an expensive leak tomorrow.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground/75">
              Our Roof Protection Plans are designed to help homeowners stay ahead of roof problems
              through scheduled inspections, preventative maintenance, drain cleaning, minor
              repairs, and detailed roof health reports.
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-foreground/75">
              Unlike a free roof inspection, these plans include ongoing maintenance and
              preventative services that help extend the life of your roof and reduce costly
              surprises.
            </p>
          </div>


          <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2 md:gap-7 lg:gap-8">

            {/* ANNUAL CARD */}
            <div className="flex flex-col rounded-2xl border-[0.5px] border-[#E0DDD6] bg-white p-7 shadow-[0_4px_24px_rgba(27,43,107,0.06)] lg:p-9">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary/70">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Annual Plan
              </div>
              <h3 className="mt-3 text-[22px] font-bold text-primary">
                Annual Roof Protection Plan
              </h3>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="text-4xl font-bold text-primary lg:text-5xl">$799</span>
                <span className="text-[15px] text-[var(--muted-foreground)]">/year</span>
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-foreground/75">
                Best for newer roofs or roofs in good condition.
              </p>

              <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-primary/70">
                Includes
              </p>
              <ul className="mt-3 space-y-2">
                {[
                  "One scheduled roof maintenance visit annually",
                  "Complete roof health inspection",
                  "Drain and scupper cleaning",
                  "Debris removal",
                  "Roof penetration inspection",
                  "Bird-hole inspection",
                  "Minor sealant repairs",
                  "Small flashing repairs",
                  "Photo documentation",
                  "Detailed roof condition report",
                  "Maintenance recommendations",
                  "Minor repairs included at no additional charge when completed during the maintenance visit",
                ].map((t) => (
                  <CheckItem key={t}>{t}</CheckItem>
                ))}
              </ul>

              <div className="mt-auto pt-8">
                <ButtonLink
                  href="/free-roof-inspection-phoenix"
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  Schedule Free Roof Health Check
                </ButtonLink>
              </div>
            </div>

            {/* BI-ANNUAL CARD — MOST POPULAR */}
            <div className="relative flex flex-col rounded-2xl bg-primary p-7 text-white shadow-[0_18px_48px_rgba(27,43,107,0.28)] ring-2 ring-accent lg:p-9 lg:scale-[1.02]">
              <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-accent px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Most Popular
              </span>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Bi-Annual Plan
              </div>
              <h3 className="mt-3 text-[22px] font-bold text-white">
                Bi-Annual Roof Protection Plan
              </h3>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="text-4xl font-bold text-accent lg:text-5xl">$999</span>
                <span className="text-[15px] text-white/70">/year</span>
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-white/80">
                Recommended for foam roofs, flat roofs, older roofs, and roofs exposed to heavy
                debris or monsoon conditions.
              </p>

              <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-white/70">
                Everything in the Annual Plan plus
              </p>
              <ul className="mt-3 space-y-2">
                {[
                  "Two scheduled maintenance visits per year",
                  "Pre-monsoon inspection and maintenance",
                  "Post-monsoon inspection and maintenance",
                  "Earlier detection of developing roof issues",
                  "Increased protection against water intrusion",
                  "More opportunities to catch minor issues before they become major repairs",
                  "Priority maintenance scheduling",
                ].map((t) => (
                  <li key={t} className="flex gap-2.5 text-[15px] leading-snug">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                    <span className="text-white/90">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-8">
                <ButtonLink
                  href="/free-roof-inspection-phoenix"
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  Schedule Free Roof Health Check
                </ButtonLink>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* COMPARISON — WHAT'S THE DIFFERENCE */}
      <section className="bg-white py-14 lg:py-16">
        <Container className="max-w-6xl">
          <h2 className="text-center text-3xl font-bold text-primary sm:text-4xl">
            What's the Difference?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-[15px] text-[var(--muted-foreground)]">
            Free inspection, protection plan, and roof warranty — three different things that work
            together.
          </p>

          <div className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-2xl shadow-[0_8px_28px_rgba(27,43,107,0.10)]">
            <ManagedImage
              pageSlug="roof-maintenance"
              slot="content-2"
              variant="content"
              className="h-full w-full object-cover"
            />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">

            {/* FREE INSPECTION */}
            <div className="rounded-xl border-[0.5px] border-[#E0DDD6] bg-white p-6 shadow-[0_4px_24px_rgba(27,43,107,0.06)]">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-foreground/5 text-foreground/70">
                  <Search className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="text-[16px] font-semibold text-primary">Free Roof Inspection</h3>
              </div>
              <ul className="mt-5 space-y-2.5">
                {["One-time evaluation", "Identifies visible roof issues", "Provides recommendations"].map((t) => (
                  <li key={t} className="flex gap-2.5 text-[14px] leading-snug">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                    <span className="text-foreground/85">{t}</span>
                  </li>
                ))}
                <li className="flex gap-2.5 text-[14px] leading-snug">
                  <Minus className="mt-0.5 h-4 w-4 shrink-0 text-foreground/40" aria-hidden="true" />
                  <span className="text-foreground/55">No maintenance included</span>
                </li>
              </ul>
            </div>

            {/* PROTECTION PLAN — emphasized */}
            <div className="rounded-xl border-[0.5px] border-accent/40 bg-white p-6 shadow-[0_10px_32px_rgba(244,121,32,0.12)] ring-1 ring-accent/40">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/15 text-accent">
                  <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="text-[16px] font-semibold text-primary">Roof Protection Plan</h3>
              </div>
              <ul className="mt-5 space-y-2.5">
                {[
                  "Ongoing preventative maintenance",
                  "Scheduled inspections",
                  "Drain cleaning",
                  "Debris removal",
                  "Minor repairs included",
                  "Photo reports",
                  "Helps prevent leaks",
                ].map((t) => (
                  <li key={t} className="flex gap-2.5 text-[14px] leading-snug">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                    <span className="text-foreground/85">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* WARRANTY */}
            <div className="rounded-xl border-[0.5px] border-[#E0DDD6] bg-white p-6 shadow-[0_4px_24px_rgba(27,43,107,0.06)]">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
                  <FileWarning className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="text-[16px] font-semibold text-primary">Roof Warranty</h3>
              </div>
              <ul className="mt-5 space-y-2.5">
                {["Covers Durafoam workmanship", "Covers installed roofing systems", "Covers eligible warranty items"].map((t) => (
                  <li key={t} className="flex gap-2.5 text-[14px] leading-snug">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                    <span className="text-foreground/85">{t}</span>
                  </li>
                ))}
                <li className="flex gap-2.5 text-[14px] leading-snug">
                  <Minus className="mt-0.5 h-4 w-4 shrink-0 text-foreground/40" aria-hidden="true" />
                  <span className="text-foreground/55">Does not cover maintenance-related issues</span>
                </li>
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* WARRANTY CLARIFICATION CALLOUT */}
      <section className="bg-[#FAEEDA] py-14 lg:py-16">
        <Container className="max-w-4xl">
          <div className="rounded-2xl border-l-4 border-accent bg-white p-7 shadow-[0_4px_24px_rgba(27,43,107,0.06)] lg:p-9">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
              <FileWarning className="h-4 w-4" aria-hidden="true" /> Important Distinction
            </div>
            <h3 className="mt-3 text-2xl font-bold text-primary sm:text-[26px]">
              Maintenance Plans and Roof Warranties Are Different
            </h3>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground/85">
              Many homeowners assume their roof warranty covers every roof-related problem.
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-foreground/85">
              In reality, most roof warranties do not cover issues caused by:
            </p>
            <ul className="mt-4 space-y-2">
              {[
                "Clogged drains or scuppers",
                "Standing or ponding water (due to clogged drains, etc.)",
                "Debris accumulation",
                "Bird damage",
                "Damage caused by other contractors",
                "Neglected maintenance items",
                "Minor issues that worsen over time",
              ].map((t) => (
                <li key={t} className="flex gap-2.5 text-[15px] leading-snug">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-red-700/80" aria-hidden="true" />
                  <span className="text-foreground/85">{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[15px] font-medium leading-relaxed text-foreground">
              Our Roof Protection Plans are specifically designed to help prevent these types of
              problems before they lead to costly repairs.
            </p>
          </div>
        </Container>
      </section>

      {/* STRONG CTA — FREE ROOF HEALTH CHECK */}
      <section className="bg-primary py-16 text-white lg:py-20">
        <Container className="max-w-5xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Start With a Free Roof Health Check
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-white/80">
              Not sure which plan is right for your roof?
            </p>
            <p className="mx-auto mt-3 max-w-3xl text-white/75">
              We'll perform a free roof health evaluation and recommend the maintenance plan that
              makes the most sense based on:
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                "Roof type",
                "Roof age",
                "Roof size",
                "Tree coverage",
                "Drainage conditions",
                "Overall roof condition",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2.5 rounded-lg bg-white/5 px-4 py-3 text-[15px]">
                  <Check className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                  <span className="text-white/90">{t}</span>
                </li>
              ))}
            </ul>

            <div className="rounded-xl bg-white/5 p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                Pricing notice
              </p>
              <p className="mt-3 text-[14px] leading-relaxed text-white/80">
                Maintenance plan pricing shown reflects our most common residential roofs.
              </p>
              <p className="mt-3 text-[14px] leading-relaxed text-white/80">
                Final pricing may be slightly higher or lower depending on roof size, roof type,
                accessibility, and overall condition.
              </p>
              <p className="mt-3 text-[14px] leading-relaxed text-white/80">
                A free roof health check is required before enrollment so we can recommend the
                right level of protection for your home.
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule My Free Roof Health Check
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" /> Call Durafoam Roofing
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* INTRO */}
      <section className="bg-white py-14">
        <Container className="max-w-3xl">
          <h2 className="text-2xl font-bold text-primary sm:text-3xl">
            Why roof maintenance matters in Phoenix
          </h2>
          <p className="mt-5 text-[17px] leading-relaxed text-foreground/85">
            Phoenix roofs face a unique combination of stresses that no other climate delivers —
            surface temperatures over 160°F in summer, monsoon storms that can drop an inch of rain
            in 20 minutes, and constant UV degradation that breaks down coatings, sealants, and
            underlayment year after year. The cost of catching a small problem — a cracked flashing,
            a minor bird hole, a failing sealant around an AC penetration — during a scheduled
            maintenance visit is a fraction of what that same problem costs after monsoon season has
            pushed water into your decking, insulation, and interior. Roof maintenance is not a
            luxury. For Phoenix homeowners, it is basic property protection.
          </p>
        </Container>
      </section>

      {/* TIMING */}
      <section className="bg-white py-14">
        <Container className="max-w-5xl">
          <h2 className="text-center text-2xl font-bold text-primary sm:text-3xl">
            When we recommend your two annual visits
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-xl border-[0.5px] border-[#E0DDD6] bg-white p-6 shadow-[0_4px_24px_rgba(27,43,107,0.06)]">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-accent/10 text-accent">
                <Sun className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-primary">
                Pre-monsoon inspection (March–May)
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-foreground/85">
                The most critical visit of the year. Phoenix monsoon season starts in July and can
                bring intense rain in minutes. We check every penetration, drain, scupper, and
                flashing before the storms arrive. Bird activity peaks in spring — we find and patch
                any holes before they become entry points for water. Foam coating is assessed for
                recoat timing. This is when small fixes prevent summer emergency calls.
              </p>
            </div>
            <div className="rounded-xl border-[0.5px] border-[#E0DDD6] bg-white p-6 shadow-[0_4px_24px_rgba(27,43,107,0.06)]">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary">
                <Leaf className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-primary">
                Post-monsoon inspection (October–November)
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-foreground/85">
                After monsoon season ends we assess what the summer did to your roof. Monsoon storms
                stress every sealant and flashing. UV damage from the hottest months shows in foam
                coating condition. We document anything that developed during summer and address
                minor items before winter. This is your annual condition report and planning visit.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* COMMERCIAL */}
      <section className="bg-primary py-14 text-white lg:py-16">
        <Container className="max-w-4xl">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Apartments, HOAs, condos, and commercial buildings
          </h2>
          <p className="mt-5 text-[17px] leading-relaxed text-white/85">
            Every commercial roof and multi-unit property is different — different roof types,
            different ages, different budgets, and different risk profiles. We do not offer a
            one-size-fits-all commercial maintenance plan because one size never fits all. What we
            do offer is a free on-site consultation where we walk every roof on your property,
            document current conditions with photos, and put together a maintenance proposal that
            makes sense for your specific buildings.
          </p>

          <ul className="mt-9 grid grid-cols-1 gap-4 md:grid-cols-2">
            {audiences.map(({ Icon, title, body }) => (
              <li key={title} className="rounded-xl bg-white/5 p-5">
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-accent/15 text-accent">
                    <Icon className="h-4.5 w-4.5" aria-hidden="true" />
                  </span>
                  <h3 className="text-[15px] font-semibold text-white">{title}</h3>
                </div>
                <p className="mt-3 text-[14px] leading-relaxed text-white/80">{body}</p>
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule a free commercial roof consultation
            </ButtonLink>
            <p className="text-sm text-white/75">
              Or call us at{" "}
              <a href="tel:+14809415373" className="font-semibold text-white underline-offset-2 hover:underline">
                480-941-5373
              </a>{" "}
              to discuss your property
            </p>
          </div>
        </Container>
      </section>

      {/* PREVENTION COMPARISON */}
      <section className="bg-[var(--surface)] py-14 lg:py-16">
        <Container className="max-w-6xl">
          <h2 className="text-center text-2xl font-bold text-primary sm:text-3xl">
            What maintenance prevents
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-[15px] text-[var(--muted-foreground)]">
            The small problem vs the expensive outcome it becomes if ignored.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {prevention.map((p, i) => (
              <div
                key={i}
                className="rounded-xl border-[0.5px] border-[#E0DDD6] bg-white p-6 shadow-[0_4px_24px_rgba(27,43,107,0.06)]"
              >
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-green-700">
                    Small problem
                  </p>
                  <p className="mt-2 text-[14px] leading-relaxed text-foreground/85">{p.small}</p>
                </div>
                <div className="mt-5 border-l-4 border-red-500 pl-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-red-700">
                    Ignored outcome
                  </p>
                  <p className="mt-2 text-[14px] leading-relaxed text-foreground/85">{p.big}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14 lg:py-16">
        <Container className="max-w-3xl">
          <h2 className="text-center text-2xl font-bold text-primary sm:text-3xl">
            Common questions about roof maintenance
          </h2>
          <div className="mt-10 space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-xl border-[0.5px] border-[#E0DDD6] bg-white p-5">
                <h3 className="text-[16px] font-semibold text-primary">{f.q}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground/85">{f.a}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold sm:text-3xl">
            Ready to protect your roof year-round?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/75">
            Start with a free inspection. We will assess your roof and put together a maintenance
            plan that makes sense for your property.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule free inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" /> Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}
